import React, { useState } from 'react';
import ButtonGroup from '@eika-infrastruktur/ui-button-group';
import { useConfig } from './default-config';
import Start from './start';
import Results from './results';
import Advisor from './advisor';

const CarInsuranceCalc = () => {
    const { links, texts, questions } = useConfig();
    enum State {
        START,
        QUESTION,
        RESULT
    }

    const [state, setState] = useState(State.START);
    const [score, updateScore] = useState(0);
    const [questionNumber, setQuestionNumber] = useState(1);
    const [currentQuestion, setNextQuestion] = useState(0);
    const [showQuestion7, setShowQuestion7] = useState(true);
    const [selectedValue, setSelected] = useState(0);
    const entries = Object.entries(questions).sort();
    const numberOfQuestions = entries.length;
    const [answers, setAnswers] = useState(Array(8).fill(0));

    const { question } = entries[currentQuestion][1];
    const alternatives = entries[currentQuestion][1].alternatives.split('|');
    const descriptions = entries[currentQuestion][1].descriptions.split('|');
    const { clarification } = entries[currentQuestion][1];
    const scores = entries.map(entry => entry[1].scores.split('|').map(value => parseInt(value, 10)));

    const values: { text: string; value: number }[] = [];

    alternatives.forEach((element: string, index: number) => {
        values.push({
            text: element,
            value: index + 1
        });
    });

    const onChange = (event: { value: string }) => {
        const selected = parseInt(event.value, 10);
        setSelected(selected);
    };

    const getNextQuestion = () => {
        if (currentQuestion === 5 && selectedValue === 2) {
            setNextQuestion(currentQuestion + 2);
            setQuestionNumber(questionNumber + 2);
            setShowQuestion7(false);
            return;
        }
        setNextQuestion(currentQuestion + 1);
        setQuestionNumber(questionNumber + 1);
    };
    const getScore = (questionNumber: number, answer: number) => {
        if (questionNumber === 6 && !showQuestion7) {
            return 0;
        }
        return scores[questionNumber][answer - 1];
    };
    const getFinalScore = (answers: number[]) => {
        return answers.reduce((accumulator: number, answer: number, i: number) => accumulator + getScore(i, answer), 0);
    };
    const setFinalScore = () => {
        setAnswers(answers => {
            updateScore(score => score + getFinalScore(answers));
            return answers;
        });
    };
    const onClickNext = () => {
        const newAnswers = answers.slice();
        newAnswers[questionNumber - 1] = selectedValue;
        setAnswers(newAnswers);
        if (questionNumber < numberOfQuestions) getNextQuestion();
        setQuestionNumber(questionNumber => {
            setSelected(answers[questionNumber - 1]);
            return questionNumber;
        });
        if (questionNumber === numberOfQuestions) {
            setFinalScore();
            setState(State.RESULT);
        }
    };

    const getPreviousQuestion = () => {
        if (currentQuestion === 7 && !showQuestion7) {
            setShowQuestion7(true);
            setQuestionNumber(questionNumber - 2);
            setNextQuestion(currentQuestion - 2);
            return;
        }
        setQuestionNumber(questionNumber - 1);
        setNextQuestion(currentQuestion - 1);
    };

    const onClickBack = () => {
        getPreviousQuestion();
        setQuestionNumber(questionNumber => {
            const answer = answers[questionNumber - 1];
            setSelected(answer);
            return questionNumber;
        });
    };

    const refreshState = () => {
        setState(State.START);
        updateScore(0);
        setQuestionNumber(1);
        setNextQuestion(0);
        setSelected(0);
        setAnswers(Array(numberOfQuestions).fill(0));
        setShowQuestion7(true);
    };

    return (
        <article className="car-insurance-calc card">
            <div className="card-content">
                {state === State.START && <Start onClick={() => setState(State.QUESTION)} />}
                {state === State.RESULT && <Results score={score} onClick={refreshState} />}
                {state === State.QUESTION && (
                    <>
                        <div className="progress-text">
                            <span className="theme-text-lightest">{questionNumber}</span>/{numberOfQuestions}
                        </div>
                        <p className="question">{question}</p>
                        <ButtonGroup selectedValue={selectedValue} onChange={onChange} values={values} />
                        <div className="footer-container">
                            {selectedValue !== 0 && (
                                <Advisor description={descriptions[selectedValue - 1]} key={selectedValue} />
                            )}
                            <div className="navigation-buttons">
                                {questionNumber > 1 && (
                                    <button type="button" className="btn btn-flat" onClick={onClickBack}>
                                        {texts.buttonBackText}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-action"
                                    onClick={onClickNext}
                                    disabled={selectedValue === 0}
                                >
                                    {questionNumber < numberOfQuestions && texts.buttonNextText}
                                    {questionNumber === numberOfQuestions && texts.buttonSeeResultText}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
            {links.disclaimerLink && (
                <small className="disclaimer">
                    {texts.disclaimer1}
                    <a href={links.disclaimerLink.url}>{links.disclaimerLink.text}</a>
                    {texts.disclaimer2}
                </small>
            )}
            {clarification && <small className="clarification">{clarification}</small>}
        </article>
    );
};

export default CarInsuranceCalc;
